import { BookOpen, Sparkles, Brain, Mic, PenTool, Palette, Volume2, X } from 'lucide-react';
import { useState } from 'react';

export default function Programs() {
  const [selectedProgram, setSelectedProgram] = useState(null);

  const mainPrograms = [
    {
      name: 'Program A: Academic Support Program',
      age: 'Classes - Nursery to Class 5',
      icon: BookOpen,
      color: 'from-purple-primary/20 to-purple-primary/40',
      description: 'Complete after-school academic care including homework, revision, reading practice, and concept clarity in all school subjects.',
      features: ['Homework Completion', 'Revision', 'Concept Clarity'],
      details: {
        overview: 'Our Academic Support Program provides comprehensive after-school care to ensure every child completes their homework, revises concepts, and develops strong academic foundations.',
        curriculum: [
          'Daily homework completion and verification',
          'Subject-wise concept revision and clarification',
          'Reading practice and comprehension building',
          'Math problem-solving strategies',
          'Language skills enhancement',
          'Science and social studies support'
        ],
        methodology: 'One-on-one guidance, peer learning, interactive sessions, and personalized attention to each child\'s learning pace and style.',
        outcomes: 'Improved academic performance, better homework habits, stronger conceptual understanding, and increased confidence in school subjects.'
      }
    },
    {
      name: 'Program B: Skill Development Program',
      age: 'Classes - Nursery to Class 5',
      icon: Sparkles,
      color: 'from-pink-light/20 to-pink-light/40',
      description: 'Specialized skill-based programs designed to enhance brain development, confidence, and creativity.',
      features: ['Vedic Maths', 'Public Speaking', 'Creative Skills'],
      details: {
        overview: 'Our Skill Development Program focuses on building essential life skills that complement academics and foster all-around development.',
        skills: ['Vedic Mathematics', 'Public Speaking & Communication', 'Cursive Handwriting', 'Art & Craft', 'Phonics Sound Learning'],
        methodology: 'Interactive workshops, practical exercises, group activities, and individual coaching to develop confidence and expertise.',
        outcomes: 'Enhanced mental abilities, improved communication skills, increased creativity, better presentation abilities, and overall personality development.'
      }
    },
  ];

  const skillPrograms = [
    {
      name: 'Vedic Maths',
      icon: Brain,
      description: 'Improves calculation speed using simple techniques, making maths faster and easier without finger counting',
      benefits: ['Boosts mental calculation speed', 'Improves accuracy and confidence in exams', 'Reduces fear of maths'],
      bgColor: '#E3F2FD',
      category: 'Mathematics & Numeracy',
      details: {
        overview: 'Vedic Mathematics uses ancient Indian mathematical techniques to simplify complex calculations and boost mental arithmetic skills.',
        techniques: ['Fast multiplication and division', 'Quick addition and subtraction', 'Square and cube calculations', 'Fraction simplification', 'Mental computation strategies'],
        benefits_list: 'Students learn to solve mathematical problems with speed and accuracy, significantly reducing calculation time and building confidence in mathematics.',
        duration: 'Varies based on proficiency level - typically 8-12 weeks for basic mastery',
        ageGroup: 'Best suited for classes 2-5'
      }
    },
    {
      name: 'Public Speaking & Conversation',
      icon: Mic,
      description: 'Develops confident speaking, clear expression, and strong communication skills through regular practice',
      benefits: ['Builds confidence to speak in front of others', 'Improves English speaking and sentence formation', 'Enhances personality and presentation skills'],
      bgColor: '#FFFDE7',
      category: 'Communication & Leadership',
      details: {
        overview: 'This program helps children overcome stage fright and develop excellent communication skills through structured practice and mentoring.',
        modules: ['Overcoming nervousness', 'Clear articulation and pronunciation', 'Storytelling techniques', 'Presentation skills', 'Group discussions', 'Extempore speaking'],
        methodology: 'Regular practice sessions, mock presentations, feedback-based learning, and confidence-building activities.',
        outcomes: 'Improved confidence, better communication abilities, strong presentation skills, and enhanced personality.',
        ageGroup: 'Suitable for classes 1-5'
      }
    },
    {
      name: 'Cursive Handwriting Learning',
      icon: PenTool,
      description: 'Teaches neat, structured, and fluent cursive writing with proper letter formation',
      benefits: ['Improves writing speed and neatness', 'Enhances presentation in school work', 'Develops fine motor skills and focus'],
      bgColor: '#FCE4EC',
      category: 'Writing & Fine Motor Skills',
      details: {
        overview: 'A comprehensive handwriting program that teaches proper letter formation, spacing, and flow for beautiful cursive writing.',
        focus_areas: ['Proper grip and posture', 'Letter formation and consistency', 'Word spacing and alignment', 'Speed and fluency', 'Handwriting improvement for exams'],
        benefits_list: 'Better presentation in school assignments, improved writing speed during exams, development of fine motor skills, and increased focus and concentration.',
        duration: 'Progressive learning - typically 6-8 weeks for significant improvement',
        ageGroup: ['Classes 1-4 (introductory)', 'Classes 5+ (advanced)']
      }
    },
    {
      name: 'Art & Craft',
      icon: Palette,
      description: 'Encourages creativity through drawing, coloring, and hands-on craft activities',
      benefits: ['Boosts creativity and imagination', 'Improves hand coordination', 'Helps in relaxation and stress-free learning'],
      bgColor: '#F3E5F5',
      category: 'Creative Arts & Expression',
      details: {
        overview: 'An engaging art program that nurtures creativity, self-expression, and artistic skills through various art forms and craft activities.',
        activities: ['Drawing techniques', 'Painting and coloring', 'Craft projects (origami, paper crafts, clay)', 'DIY projects', 'Theme-based art activities', 'Mixed media exploration'],
        development: 'Enhances creative thinking, improves hand-eye coordination, develops artistic appreciation, builds confidence in self-expression.',
        benefits_list: 'Stress relief, improved creativity, better motor skills, increased patience, and development of artistic talents.',
        ageGroup: 'All ages from Nursery to Class 5'
      }
    },
    {
      name: 'Phonics Sound Learning',
      icon: Volume2,
      description: 'Complete phonics program focusing on letter sounds, blending, and reading skills',
      benefits: ['Improves reading and pronunciation', 'Builds strong English foundation', 'Helps children read independently'],
      bgColor: '#E8F5E9',
      category: 'Reading & Language Foundation',
      details: {
        overview: 'A structured phonics program that builds a strong foundation in English by teaching letter sounds, blending, and decoding strategies.',
        components: ['Letter sound recognition', 'Sound blending', 'CVC (consonant-vowel-consonant) words', 'Digraphs and trigraphs', 'Sight words', 'Reading fluency'],
        methodology: 'Interactive lessons, sound repetition, reading practice, engaging activities, and progressive difficulty levels.',
        outcomes: 'Strong English foundation, independent reading ability, improved pronunciation, enhanced confidence in communication.',
        ageGroup: 'Best for classes KG-2, but beneficial for older kids needing foundation strengthening'
      }
    },
  ];

  return (
    <section id="programs" className="py-16 sm:py-24 bg-pink-light">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12 sm:mb-16">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold font-display text-purple-dark mb-4">
            Our <span className="text-purple-primary">Programs</span>
          </h2>
          <p className="text-gray-600 text-base sm:text-lg max-w-2xl mx-auto">
            Two structured programs designed for academic excellence and skill growth
          </p>
        </div>

        {/* Main Programs Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8 mb-16 sm:mb-24">
          {mainPrograms.map((program, index) => {
            const Icon = program.icon;
            return (
              <div
                key={index}
                className="group bg-white rounded-2xl shadow-soft hover:shadow-lg transition-all duration-300 overflow-hidden transform hover:scale-105"
              >
                {/* Header with gradient */}
                <div className={`bg-gradient-to-r ${program.color} h-20 sm:h-24 flex items-center justify-center`}>
                  <Icon size={48} className="text-white opacity-80" />
                </div>

                {/* Content */}
                <div className="p-6 sm:p-8">
                  <h3 className="text-2xl sm:text-3xl font-bold font-display text-gray-800 mb-1">
                    {program.name}
                  </h3>
                  <p className="text-sm sm:text-base text-gray-500 font-medium mb-4">
                    {program.age}
                  </p>
                  <p className="text-gray-600 text-sm sm:text-base leading-relaxed mb-4">
                    {program.description}
                  </p>

                  {/* Features */}
                  <div className="flex flex-wrap gap-2">
                    {program.features.map((feature, idx) => (
                      <span
                        key={idx}
                        className="text-xs sm:text-sm bg-pink-light/50 text-purple-dark px-3 py-1 rounded-full font-medium"
                      >
                        {feature}
                      </span>
                    ))}
                  </div>
                </div>

                {/* CTA Button */}
                <div className="px-6 sm:px-8 pb-6 sm:pb-8">
                  <button
                    onClick={() => setSelectedProgram(program)}
                    className="w-full bg-purple-primary hover:bg-purple-dark text-white py-2.5 sm:py-3 rounded-lg font-semibold text-sm sm:text-base hover:shadow-lg transition-all duration-300"
                  >
                    Learn More
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {/* Program B Details */}
        <div className="mb-16 sm:mb-24">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold font-display text-purple-dark mb-4">
              Skill Development <span className="text-purple-primary">Programs</span>
            </h2>
            <p className="text-gray-600 text-base sm:text-lg max-w-2xl mx-auto">
              5 specialized programs to enhance brain development, confidence, and creativity
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            {skillPrograms.map((skill, index) => {
              const Icon = skill.icon;
              return (
                <div key={index} style={{ backgroundColor: skill.bgColor }} className="rounded-2xl p-6 sm:p-8 shadow-soft hover:shadow-lg transition-all duration-300 border-l-4 border-purple-primary">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="bg-gradient-to-br from-purple-primary/20 to-pink-light/30 w-14 h-14 sm:w-16 sm:h-16 rounded-xl flex items-center justify-center">
                      <Icon className="text-purple-primary" size={28} />
                    </div>
                    <h3 className="text-xl sm:text-2xl font-bold text-gray-800 font-display">
                      {skill.name}
                    </h3>
                  </div>
                  
                  {/* Category Badge */}
                  <div className="mb-3">
                    <span className="inline-block bg-purple-primary/20 text-purple-primary text-xs font-semibold px-3 py-1 rounded-full">
                      {skill.category}
                    </span>
                  </div>

                  <p className="text-gray-600 text-sm sm:text-base leading-relaxed mb-4">
                    {skill.description}
                  </p>
                  <div>
                    <p className="text-sm font-semibold text-gray-800 mb-2">Benefits:</p>
                    <ul className="space-y-2">
                      {skill.benefits.map((benefit, idx) => (
                        <li key={idx} className="text-xs sm:text-sm text-gray-600 flex items-start gap-2">
                          <span className="text-purple-primary font-bold mt-1">•</span>
                          <span>{benefit}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <button
                    onClick={() => setSelectedProgram(skill)}
                    className="w-full mt-6 bg-purple-primary hover:bg-purple-dark text-white py-2.5 sm:py-3 rounded-lg font-semibold text-sm sm:text-base hover:shadow-lg transition-all duration-300"
                  >
                    Learn More
                  </button>
                </div>
              );
            })}
          </div>
        </div>

        {/* Program Details Modal */}
        {selectedProgram && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
              {/* Header */}
              <div className="sticky top-0 bg-gradient-to-r from-purple-primary to-purple-dark p-6 sm:p-8 flex items-center justify-between">
                <h2 className="text-2xl sm:text-3xl font-bold text-white font-display pr-4">
                  {selectedProgram.name}
                </h2>
                <button
                  onClick={() => setSelectedProgram(null)}
                  className="text-white hover:bg-white/20 p-2 rounded-lg transition-colors flex-shrink-0"
                >
                  <X size={24} />
                </button>
              </div>

              {/* Content */}
              <div className="p-6 sm:p-8 space-y-6">
                {/* Overview */}
                <div>
                  <h3 className="text-xl font-bold text-gray-800 mb-3">Overview</h3>
                  <p className="text-gray-600 leading-relaxed">
                    {selectedProgram.details.overview}
                  </p>
                </div>

                {/* Curriculum/Modules/Activities */}
                {(selectedProgram.details.curriculum || selectedProgram.details.modules || selectedProgram.details.skills || selectedProgram.details.activities || selectedProgram.details.components) && (
                  <div>
                    <h3 className="text-xl font-bold text-gray-800 mb-3">
                      {selectedProgram.details.curriculum ? 'Curriculum' : selectedProgram.details.modules ? 'Modules' : selectedProgram.details.skills ? 'Skills Covered' : selectedProgram.details.activities ? 'Activities' : 'Components'}
                    </h3>
                    <ul className="space-y-2">
                      {(selectedProgram.details.curriculum || selectedProgram.details.modules || selectedProgram.details.skills || selectedProgram.details.activities || selectedProgram.details.components)?.map((item, idx) => (
                        <li key={idx} className="flex items-start gap-3 text-gray-600">
                          <span className="text-purple-primary font-bold mt-1">✓</span>
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Methodology */}
                {selectedProgram.details.methodology && (
                  <div>
                    <h3 className="text-xl font-bold text-gray-800 mb-3">Methodology</h3>
                    <p className="text-gray-600 leading-relaxed">
                      {selectedProgram.details.methodology}
                    </p>
                  </div>
                )}

                {/* Techniques */}
                {selectedProgram.details.techniques && (
                  <div>
                    <h3 className="text-xl font-bold text-gray-800 mb-3">Techniques</h3>
                    <ul className="space-y-2">
                      {selectedProgram.details.techniques.map((item, idx) => (
                        <li key={idx} className="flex items-start gap-3 text-gray-600">
                          <span className="text-purple-primary font-bold">✓</span>
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Benefits List */}
                {selectedProgram.details.benefits_list && (
                  <div>
                    <h3 className="text-xl font-bold text-gray-800 mb-3">Benefits</h3>
                    <p className="text-gray-600 leading-relaxed">
                      {selectedProgram.details.benefits_list}
                    </p>
                  </div>
                )}

                {/* Outcomes */}
                {selectedProgram.details.outcomes && (
                  <div>
                    <h3 className="text-xl font-bold text-gray-800 mb-3">Expected Outcomes</h3>
                    <p className="text-gray-600 leading-relaxed">
                      {selectedProgram.details.outcomes}
                    </p>
                  </div>
                )}

                {/* Duration */}
                {selectedProgram.details.duration && (
                  <div>
                    <h3 className="text-xl font-bold text-gray-800 mb-3">Duration</h3>
                    <p className="text-gray-600">
                      {selectedProgram.details.duration}
                    </p>
                  </div>
                )}

                {/* Age Group */}
                {selectedProgram.details.ageGroup && (
                  <div>
                    <h3 className="text-xl font-bold text-gray-800 mb-3">Recommended Age Group</h3>
                    <p className="text-gray-600">
                      {Array.isArray(selectedProgram.details.ageGroup) 
                        ? selectedProgram.details.ageGroup.join(', ')
                        : selectedProgram.details.ageGroup}
                    </p>
                  </div>
                )}

                {/* CTA Buttons */}
                <div className="flex gap-4 pt-6 border-t">
                  <button
                    onClick={() => {
                      window.open('https://docs.google.com/forms/d/e/1FAIpQLSdCRhoQCfF_9QNWPYQ-nPd1UTEWGlL4j7n4XSHVeX-HTE0_-g/viewform?usp=publish-editor', '_blank');
                      setSelectedProgram(null);
                    }}
                    className="flex-1 bg-purple-primary hover:bg-purple-dark text-white py-3 rounded-lg font-semibold transition-all duration-300"
                  >
                    Enroll Now
                  </button>
                  <button
                    onClick={() => setSelectedProgram(null)}
                    className="flex-1 border-2 border-purple-primary text-purple-primary hover:bg-purple-primary/5 py-3 rounded-lg font-semibold transition-all duration-300"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

